export type RevolutBillingAddressInput = Partial<{
  street: string | null
  streetType: string | null
  houseNumber: string | number | null
  annex: string | null
  houseNumberAddition: string | null
  building: string | null
  floor: string | null
  postalCode: string | null
  city: string | null
  region: string | null
  fullStreet: string | null
}>

export type RevolutBillingAddress = {
  countryCode: string
  postcode: string
  region?: string
  city?: string
  streetLine1?: string
  streetLine2?: string
}

const joinNonEmpty = (parts: Array<string | number | null | undefined>, separator = ' ') =>
  parts
    .map((part) => (part == null ? '' : String(part).trim()))
    .filter(Boolean)
    .join(separator)

const buildStreetLine1 = (address: RevolutBillingAddressInput): string | undefined => {
  if (address.fullStreet?.trim()) {
    return address.fullStreet.trim()
  }

  const streetPart = joinNonEmpty([address.streetType, address.street])
  const houseNumber = address.houseNumber == null ? '' : String(address.houseNumber).trim()
  const addition = (address.annex || address.houseNumberAddition || '').trim()
  const housePart = `${houseNumber}${addition}`

  const streetLine1 = joinNonEmpty([streetPart, housePart])
  return streetLine1 || undefined
}

const buildStreetLine2 = (address: RevolutBillingAddressInput): string | undefined => {
  const streetLine2 = joinNonEmpty([address.building, address.floor], ', ')
  return streetLine2 || undefined
}

/**
 * Maps an internal address shape to Revolut Checkout's Address format.
 * Returns undefined when required fields (countryCode, postcode) are missing.
 */
export const mapToRevolutBillingAddress = (
  address: RevolutBillingAddressInput | null | undefined,
  countryCode: string | null | undefined
): RevolutBillingAddress | undefined => {
  const normalizedCountryCode = countryCode?.trim().toUpperCase()
  const postcode = address?.postalCode?.trim()

  if (!normalizedCountryCode || !postcode) {
    return undefined
  }

  const streetLine1 = address ? buildStreetLine1(address) : undefined
  const streetLine2 = address ? buildStreetLine2(address) : undefined
  const city = address?.city?.trim() || undefined
  const region = address?.region?.trim() || undefined

  return {
    countryCode: normalizedCountryCode,
    postcode,
    ...(city ? { city } : {}),
    ...(region ? { region } : {}),
    ...(streetLine1 ? { streetLine1 } : {}),
    ...(streetLine2 ? { streetLine2 } : {})
  }
}
